// src/screens/MerchCheckoutScreen.js
// ============================================================================
// MERCH CHECKOUT SCREEN
//
// Sits between the store's size picker and the actual payment step. Lets the
// user choose between:
//   - Wallet:  instant, no card needed, drains gift balance first then balance
//   - Card:    Stripe Checkout (existing path), redirects to Stripe-hosted page
//
// Wallet path collects the shipping address inline (Stripe's hosted page
// collects it for the card path).
//
// Route params (from MerchSizeSelector):
//   - productKey:   MERCH_CATALOG key (e.g. 'classic_tee')
//   - productName:  display name (e.g. 'Classic CritCash Tee')
//   - size:         size key (e.g. 'L')
//   - sizeLabel:    display size (e.g. 'L', or '18″×16″' for mouse pad)
//   - priceCents:   total price in cents
//
// On wallet success → navigate to MerchOrderConfirmation { orderId, paidVia: 'wallet' }
// On Stripe confirm → redirect window.location to the Stripe checkout URL
// ============================================================================

import React, { useEffect, useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Alert,
  SafeAreaView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';
import PaymentMethodSelector from '../components/PaymentMethodSelector';
import { useCart } from '../context/CartContext';

const showAlert = (title, message) => {
  if (Platform.OS === 'web') {
    // eslint-disable-next-line no-alert
    alert(`${title}\n\n${message}`);
  } else {
    Alert.alert(title, message);
  }
};

const usd = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

// Permissive US state regex; server validates server-side too.
const STATE_RE = /^[A-Za-z]{2}$/;
const ZIP_RE = /^\d{5}(-?\d{4})?$/;

function getRouteParam(route, key) {
  return route?.params?.[key];
}

export default function MerchCheckoutScreen({ navigation, route }) {
  const { clear: clearCart } = useCart();

  // Build a normalized items[] regardless of which path got us here:
  //   A) Cart path → route.params.cart is the full array of line items.
  //      Routed from CartScreen with each line: { productKey, size,
  //      sizeLabel, productName, unitAmountCents, quantity }.
  //   B) Direct-buy path → route.params has the single-item legacy shape
  //      (productKey, size, sizeLabel, priceCents). We synthesize a
  //      single-line items[] from those params so the rest of this screen
  //      treats both paths identically.
  const items = useMemo(() => {
    const cart = getRouteParam(route, 'cart');
    if (Array.isArray(cart) && cart.length > 0) {
      return cart.map((line) => ({
        productKey: line.productKey,
        size: line.size,
        sizeLabel: line.sizeLabel || line.size,
        productName: line.productName || line.productKey,
        unitAmountCents: Number(line.unitAmountCents || 0),
        quantity: Math.max(1, Math.floor(Number(line.quantity) || 1)),
      }));
    }
    const productKey = getRouteParam(route, 'productKey');
    const size = getRouteParam(route, 'size');
    const priceCents = Number(getRouteParam(route, 'priceCents') || 0);
    if (!productKey || !size || !priceCents) return [];
    return [{
      productKey,
      size,
      sizeLabel: getRouteParam(route, 'sizeLabel') || size,
      productName: getRouteParam(route, 'productName') || 'Merch',
      unitAmountCents: priceCents,
      quantity: 1,
    }];
  }, [route]);

  const isCartPath = Array.isArray(getRouteParam(route, 'cart'));
  const priceCents = useMemo(
    () => items.reduce((s, l) => s + l.unitAmountCents * l.quantity, 0),
    [items]
  );
  const totalQty = useMemo(
    () => items.reduce((s, l) => s + l.quantity, 0),
    [items]
  );

  // Auth + balance
  const [uid, setUid] = useState(null);
  const [balanceCents, setBalanceCents] = useState(0);
  const [giftBalanceCents, setGiftBalanceCents] = useState(0);
  const [email, setEmail] = useState('');
  const [defaultName, setDefaultName] = useState('');
  const [balanceLoading, setBalanceLoading] = useState(true);

  // Selection
  const [paymentMethod, setPaymentMethod] = useState(null); // 'wallet' | 'stripe'

  // Shipping form (wallet path only)
  const [shipName, setShipName] = useState('');
  const [shipLine1, setShipLine1] = useState('');
  const [shipLine2, setShipLine2] = useState('');
  const [shipCity, setShipCity] = useState('');
  const [shipState, setShipState] = useState('');
  const [shipZip, setShipZip] = useState('');

  const [submitting, setSubmitting] = useState(false);

  // Subscribe to user balance
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user?.uid) {
      setBalanceLoading(false);
      return;
    }
    setUid(user.uid);
    setEmail(user.email || '');

    const db = getFirestore();
    const userRef = doc(db, 'users', user.uid);
    const unsub = onSnapshot(
      userRef,
      (snap) => {
        const data = snap.data() || {};
        setBalanceCents(Number(data.balanceCents || 0));
        setGiftBalanceCents(Number(data.giftBalanceCents || 0));
        if (data.displayName) setDefaultName(data.displayName);
        else if (data.fullName) setDefaultName(data.fullName);
        else if (data.firstName || data.lastName) {
          setDefaultName(`${data.firstName || ''} ${data.lastName || ''}`.trim());
        }
        setBalanceLoading(false);
      },
      (err) => {
        console.error('[MerchCheckout] balance read failed:', err);
        setBalanceLoading(false);
      }
    );
    return unsub;
  }, []);

  // Pre-fill name field once we know it (only on initial mount of the user)
  useEffect(() => {
    if (defaultName && !shipName) setShipName(defaultName);
  }, [defaultName]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Default the payment method as soon as we know the balance:
  //   - wallet if sufficient
  //   - stripe otherwise
  useEffect(() => {
    if (balanceLoading) return;
    if (paymentMethod !== null) return;
    setPaymentMethod(balanceCents + giftBalanceCents >= priceCents ? 'wallet' : 'stripe');
  }, [balanceLoading, balanceCents, giftBalanceCents, priceCents, paymentMethod]);

  // Validate the shipping form (used for wallet path before submit).
  const shippingError = useMemo(() => {
    if (paymentMethod !== 'wallet') return null;
    if (!shipName.trim()) return 'Recipient name is required.';
    if (!shipLine1.trim()) return 'Street address is required.';
    if (!shipCity.trim()) return 'City is required.';
    if (!STATE_RE.test(shipState.trim())) return 'State must be a 2-letter code (e.g. WI).';
    if (!ZIP_RE.test(shipZip.trim())) return 'ZIP must be a valid US ZIP (5 or 9 digits).';
    return null;
  }, [paymentMethod, shipName, shipLine1, shipCity, shipState, shipZip]);

  // ============================================================================
  // SUBMIT HANDLERS
  // ============================================================================

  // Backend signature: { items: [{ productKey, size, quantity }, ...] }
  // (multi-item, new path) OR { productKey, size, quantity } (legacy single
  // item; backend still accepts it). We always send items[] since this
  // screen normalizes both paths into the same shape above.
  const itemsForBackend = useMemo(
    () => items.map((l) => ({ productKey: l.productKey, size: l.size, quantity: l.quantity })),
    [items]
  );

  const handleWalletSubmit = async () => {
    if (shippingError) {
      showAlert('Fix shipping', shippingError);
      return;
    }
    setSubmitting(true);
    try {
      const functions = getFunctions();
      const call = httpsCallable(functions, 'createMerchWalletOrder');
      const result = await call({
        items: itemsForBackend,
        recipient: {
          name: shipName.trim(),
          email: email || undefined,
          line1: shipLine1.trim(),
          line2: shipLine2.trim() || undefined,
          city: shipCity.trim(),
          state: shipState.trim().toUpperCase(),
          postal_code: shipZip.trim(),
          country: 'US',
        },
      });

      const { orderId } = result.data || {};
      if (!orderId) {
        throw new Error('Order created but no orderId returned.');
      }

      // Clear the local cart on success — the order is now persisted
      // server-side. No-op when this was a direct-buy path (cart was
      // already empty going in).
      if (isCartPath) clearCart();

      // Wallet path is fully synchronous — jump straight to the
      // confirmation screen and skip the Stripe-session polling.
      navigation.navigate('MerchOrderConfirmation', { orderId, paidVia: 'wallet' });
    } catch (err) {
      console.error('[MerchCheckout] wallet order error:', err);
      setSubmitting(false);
      const friendly =
        err?.message?.includes('Wallet balance')
          ? err.message
          : err?.message?.includes('Sold out')
            ? 'Sold out — that item is no longer available.'
            : err?.message?.includes('Founder code')
              ? 'This item requires a Founder code. Redeem yours and try again.'
              : `Couldn't complete wallet purchase: ${err.message || 'unknown error'}`;
      showAlert('Purchase failed', friendly);
    }
  };

  const handleStripeSubmit = async () => {
    setSubmitting(true);
    try {
      const functions = getFunctions();
      const call = httpsCallable(functions, 'createMerchCheckoutSession');
      const result = await call({ items: itemsForBackend });
      const { url } = result.data || {};
      if (!url) throw new Error('No checkout URL returned.');

      // Clear the local cart BEFORE the Stripe redirect. If the user
      // completes payment, the order is server-side; if they cancel, the
      // intent already converted to a server merchOrders doc and re-running
      // the cart isn't desirable (would create a second pending order).
      if (isCartPath) clearCart();

      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.location.href = url;
      } else {
        showAlert('Checkout URL', url);
        setSubmitting(false);
      }
    } catch (err) {
      console.error('[MerchCheckout] stripe checkout error:', err);
      setSubmitting(false);
      showAlert('Checkout failed', err?.message || 'Could not start Stripe checkout.');
    }
  };

  const handleSubmit = () => {
    if (paymentMethod === 'wallet') handleWalletSubmit();
    else if (paymentMethod === 'stripe') handleStripeSubmit();
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (items.length === 0 || priceCents <= 0) {
    return (
      <SafeAreaView style={styles.safe}>
        <Header navigation={navigation} />
        <View style={styles.center}>
          <Ionicons name="alert-circle-outline" size={40} color="#E74C3C" />
          <Text style={styles.errorTitle}>Missing checkout details</Text>
          <Text style={styles.errorBody}>
            Head back to the store and add an item, or pick a size again.
          </Text>
          <TouchableOpacity style={styles.backBtn} onPress={() => navigation.navigate('Store')}>
            <Text style={styles.backBtnText}>Back to Store</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  if (!uid && !balanceLoading) {
    return (
      <SafeAreaView style={styles.safe}>
        <Header navigation={navigation} />
        <View style={styles.center}>
          <Ionicons name="lock-closed-outline" size={40} color="#FCD000" />
          <Text style={styles.errorTitle}>Sign in to check out</Text>
          <Text style={styles.errorBody}>
            You need a CritCash account to purchase merch.
          </Text>
          <TouchableOpacity style={styles.backBtn} onPress={() => navigation.navigate('Login')}>
            <Text style={styles.backBtnText}>Sign In</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safe}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>Checkout</Text>

        {/* Product summary — one row per line item. Renders as a single
            row for direct-buy paths (legacy params) and as a multi-row
            list for cart-checkout paths. The Total row at the bottom is
            the sum across all lines. */}
        <View style={styles.summaryCard}>
          {items.map((line, idx) => (
            <View key={`${line.productKey}_${line.size}_${idx}`} style={styles.lineBlock}>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel} numberOfLines={2}>
                  {line.productName}{line.quantity > 1 ? `  ×${line.quantity}` : ''}
                </Text>
                <Text style={styles.summaryValue}>
                  {usd(line.unitAmountCents * line.quantity)}
                </Text>
              </View>
              {line.sizeLabel ? (
                <View style={styles.summaryRow}>
                  <Text style={styles.summarySub}>Size: {line.sizeLabel}</Text>
                  {line.quantity > 1 ? (
                    <Text style={styles.summarySub}>{usd(line.unitAmountCents)} each</Text>
                  ) : null}
                </View>
              ) : null}
            </View>
          ))}
          <View style={styles.divider} />
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              Total{totalQty > 1 ? ` (${totalQty} items)` : ''}
            </Text>
            <Text style={styles.summaryTotal}>{usd(priceCents)}</Text>
          </View>
        </View>

        {/* Payment selector */}
        <Text style={styles.sectionLabel}>Payment</Text>
        {balanceLoading ? (
          <ActivityIndicator color="#FCD000" />
        ) : (
          <PaymentMethodSelector
            priceCents={priceCents}
            walletBalanceCents={balanceCents}
            giftBalanceCents={giftBalanceCents}
            selected={paymentMethod}
            onSelect={setPaymentMethod}
            disabled={submitting}
          />
        )}

        {/* Shipping form — wallet path only. Stripe collects its own on the
            hosted checkout page. */}
        {paymentMethod === 'wallet' && (
          <>
            <Text style={styles.sectionLabel}>Shipping Address</Text>
            <View style={styles.formCard}>
              <FieldInput
                label="Recipient Name"
                value={shipName}
                onChangeText={setShipName}
                placeholder="Jane Doe"
                editable={!submitting}
              />
              <FieldInput
                label="Street Address"
                value={shipLine1}
                onChangeText={setShipLine1}
                placeholder="123 Main St"
                editable={!submitting}
              />
              <FieldInput
                label="Apt / Suite (optional)"
                value={shipLine2}
                onChangeText={setShipLine2}
                placeholder="Apt 4B"
                editable={!submitting}
              />
              <View style={styles.row}>
                <View style={{ flex: 2 }}>
                  <FieldInput
                    label="City"
                    value={shipCity}
                    onChangeText={setShipCity}
                    placeholder="Madison"
                    editable={!submitting}
                  />
                </View>
                <View style={{ flex: 1, marginLeft: 8 }}>
                  <FieldInput
                    label="State"
                    value={shipState}
                    onChangeText={(t) => setShipState(t.toUpperCase().slice(0, 2))}
                    placeholder="WI"
                    autoCapitalize="characters"
                    maxLength={2}
                    editable={!submitting}
                  />
                </View>
                <View style={{ flex: 1, marginLeft: 8 }}>
                  <FieldInput
                    label="ZIP"
                    value={shipZip}
                    onChangeText={(t) => setShipZip(t.replace(/[^0-9-]/g, '').slice(0, 10))}
                    placeholder="53703"
                    keyboardType="number-pad"
                    editable={!submitting}
                  />
                </View>
              </View>
              <Text style={styles.shippingNote}>
                US-only shipping. We never see your full address again after the order goes to Printful.
              </Text>
              {shippingError && (
                <Text style={styles.shippingError}>{shippingError}</Text>
              )}
            </View>
          </>
        )}

        {/* Submit */}
        <TouchableOpacity
          onPress={handleSubmit}
          disabled={
            submitting ||
            !paymentMethod ||
            (paymentMethod === 'wallet' && !!shippingError)
          }
          style={[
            styles.submit,
            (submitting || !paymentMethod || (paymentMethod === 'wallet' && !!shippingError)) && styles.submitDisabled,
          ]}
        >
          {submitting ? (
            <ActivityIndicator color="#000" />
          ) : (
            <Text style={styles.submitText}>
              {paymentMethod === 'wallet'
                ? `Pay ${usd(priceCents)} with Wallet`
                : paymentMethod === 'stripe'
                  ? `Continue to Stripe (${usd(priceCents)})`
                  : 'Choose a payment method'}
            </Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.cancelBtn} disabled={submitting}>
          <Text style={styles.cancelText}>Cancel</Text>
        </TouchableOpacity>
      </ScrollView>
      <Footer navigation={navigation} />
      <NavBar navigation={navigation} />
    </SafeAreaView>
  );
}

// ----------------------------------------------------------------------------
// Helper field component
// ----------------------------------------------------------------------------
function FieldInput({ label, ...inputProps }) {
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        style={styles.input}
        placeholderTextColor="#555"
        {...inputProps}
      />
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#0b0b0b' },
  container: { padding: 18, paddingBottom: 120 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },

  title: { color: '#FCD000', fontSize: 26, fontWeight: '800', marginBottom: 18, textAlign: 'center' },

  sectionLabel: {
    color: '#bbb',
    fontSize: 13,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 18,
    marginBottom: 8,
  },

  summaryCard: {
    backgroundColor: '#141414',
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: '#262626',
  },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 4 },
  lineBlock: { paddingVertical: 4, borderBottomWidth: 0 },
  summaryLabel: { color: '#fff', fontSize: 15, fontWeight: '600' },
  summaryValue: { color: '#fff', fontSize: 15, fontWeight: '700' },
  summarySub: { color: '#888', fontSize: 13 },
  summaryTotal: { color: '#FCD000', fontSize: 17, fontWeight: '800' },
  divider: { height: 1, backgroundColor: '#262626', marginVertical: 8 },

  formCard: {
    backgroundColor: '#141414',
    borderRadius: 10,
    padding: 14,
    borderWidth: 1,
    borderColor: '#262626',
  },
  field: { marginBottom: 10 },
  fieldLabel: { color: '#888', fontSize: 12, marginBottom: 4 },
  input: {
    backgroundColor: '#1d1d1d',
    color: '#fff',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#2a2a2a',
    fontSize: 15,
  },
  row: { flexDirection: 'row' },
  shippingNote: { color: '#666', fontSize: 11, marginTop: 6, fontStyle: 'italic' },
  shippingError: { color: '#E74C3C', fontSize: 12, marginTop: 6, fontWeight: '600' },

  submit: {
    marginTop: 22,
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  submitDisabled: { backgroundColor: '#333' },
  submitText: { color: '#000', fontSize: 16, fontWeight: '800' },

  cancelBtn: { marginTop: 10, paddingVertical: 12, alignItems: 'center' },
  cancelText: { color: '#888', fontSize: 14 },

  errorTitle: { color: '#fff', fontSize: 18, fontWeight: '700', marginTop: 12 },
  errorBody: { color: '#bbb', fontSize: 14, textAlign: 'center', marginTop: 6 },
  backBtn: {
    marginTop: 16,
    backgroundColor: '#FCD000',
    paddingVertical: 10,
    paddingHorizontal: 18,
    borderRadius: 8,
  },
  backBtnText: { color: '#000', fontWeight: '700' },
});
